import React, { useEffect, useState, useRef, useCallback } from "react";
import apiClient from "../apiClient";
import IonIcon from "./IonIcon";

export default function GasSensorPanel({
  workplaceId,
  pollMs = 5000, // 데이터 새로고침
  autoRotateMs = 5000, // tab자동 변환주기
  gap = 8, // 탭 사이 간격(px)
  idleMs = 30000, // 사용자가 한번 tab 클릭한 이후에 다른액션없는 시간 (다시 자동순환처리 하려고)
}) {
  const [tabs, setTabs] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [data, setData] = useState(null);

  const listRef = useRef(null);
  const autoRef = useRef(null);

  const levelClass = (lvl) => {
    const v = (lvl ?? "").toString().toLowerCase();
    if (v === "danger") return "level-danger";
    if (v === "warn") return "level-warn";
    if (v === "normal") return "";
    return ""; // 값 없거나 매칭 안 되면 기본 스타일
  };
  // 최근 사용자 클릭 시각
  const lastActionAtRef = useRef(0);
  const markUserClick = useCallback(() => {
    if (idleMs > 0) lastActionAtRef.current = Date.now();
  }, [idleMs]);

  // 자동 순환 중에는 hover를 비활성화해서 "회색 hover"가 이전 탭에 남아 보이지 않게
  const [disableHover, setDisableHover] = useState(false);
  const envLevel = (key) => (data ? levelClass(data[key]) : "");
  // 탭 목록
  const fetchTabs = useCallback(async () => {
    try {
      const { data } = await apiClient.post("/api/GasSensor/gassensor-info", {
        WorkplaceId: workplaceId,
        GasSensorID: 0,
      });
      const res = typeof data === "string" ? JSON.parse(data) : data;
      const raw = Array.isArray(res?.data)
        ? res.data
        : res?.data
        ? [res.data]
        : [];

      const mapped = raw.map((g, i) => ({
        id: g.gasSensorID ?? i,
        name: g.gasSensorName ?? `센서 ${i + 1}`,
        _raw: g,
      }));

      setTabs(mapped);
      setSelectedId(
        (prev) => mapped.find((t) => t.id === prev)?.id ?? mapped[0]?.id ?? null
      );
    } catch (err) {
      console.error("가스 센서 탭 목록 가져오기 실패:", err);
      setTabs([]);
      setSelectedId(null);
    }
  }, [workplaceId]);

  // 센서 데이터
  const fetchData = useCallback(
    async (gasSensorId) => {
      try {
        const { data } = await apiClient.post("/api/GasSensor/gassensor-info", {
          WorkplaceId: workplaceId,
          GasSensorID: gasSensorId,
        });
        const res = typeof data === "string" ? JSON.parse(data) : data;

        let sensorData = null;
        if (Array.isArray(res?.data) && res.data.length > 0)
          sensorData = res.data[0];
        else if (res?.data) sensorData = res.data;

        setData(sensorData);
      } catch (err) {
        console.error("가스 센서 데이터 가져오기 실패:", err);
        setData(null);
      }
    },
    [workplaceId]
  );

  useEffect(() => {
    if (!workplaceId) return;
    fetchTabs();
  }, [workplaceId, fetchTabs]);

  useEffect(() => {
    if (!workplaceId || selectedId == null) return;

    fetchData(selectedId);

    const id = setInterval(() => fetchData(selectedId), pollMs);
    return () => clearInterval(id);
  }, [workplaceId, selectedId, pollMs, fetchData]);

  // 선택된 탭이 보이도록 스크롤
  const scrollSelectedIntoView = useCallback(() => {
    const wrap = listRef.current;
    if (!wrap) return;
    const btn = wrap.querySelector(`[data-tab-id="${selectedId}"]`);
    if (!btn) return;

    btn.scrollIntoView({
      behavior: "smooth",
      inline: "nearest",
      block: "nearest",
    });
  }, [selectedId]);

  useEffect(() => {
    scrollSelectedIntoView();
  }, [selectedId, tabs.length, scrollSelectedIntoView]);

  // 자동 순환: 클릭 후 idleMs 동안만 일시정지, 그 후 자동순환 재개
  useEffect(() => {
    if (autoRotateMs <= 0 || tabs.length <= 1) return;
    clearInterval(autoRef.current);

    autoRef.current = setInterval(() => {
      const now = Date.now();
      const hasRecentClick =
        lastActionAtRef.current > 0 && now - lastActionAtRef.current < idleMs;

      // idle 상태면(=자동순환 중) hover 비활성화, 아니면 활성화
      setDisableHover(!hasRecentClick);

      if (hasRecentClick) return; // 아직 idle 아님 → 자동순환 일시정지

      setSelectedId((prev) => {
        const idx = tabs.findIndex((t) => t.id === prev);
        const next = tabs[(idx >= 0 ? idx + 1 : 0) % tabs.length];
        return next?.id ?? prev;
      });
    }, autoRotateMs);

    return () => clearInterval(autoRef.current);
  }, [autoRotateMs, idleMs, tabs]);

  const btnWidth = `calc((100% - ${gap * 3}px) / 4)`;
  const dragThreshold = 5; // px: 이 이상 움직여야 드래그로 간주
  const isDragRef = useRef(false);
  const startedDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startScrollRef = useRef(0);

  const onPointerDown = (e) => {
    const el = listRef.current;
    if (!el) return;
    isDragRef.current = true;
    startedDraggingRef.current = false;
    startXRef.current = e.clientX;
    startScrollRef.current = el.scrollLeft;
    el.classList.add("dragging");
  };

  const onPointerMove = (e) => {
    if (!isDragRef.current) return;
    const el = listRef.current;
    const dx = e.clientX - startXRef.current;

    if (Math.abs(dx) >= dragThreshold) {
      startedDraggingRef.current = true;
      el.scrollLeft = startScrollRef.current - dx; // 드래그 방향대로 이동
      e.preventDefault(); // 텍스트 선택/드래그 방지
    }
  };

  const onPointerUpOrLeave = () => {
    isDragRef.current = false;
    listRef.current?.classList.remove("dragging");
  };
  return (
    <>
      <div className="gas_sensor_inner">
        {" "}
        <div
          className={`btns small gas_sensor_tabs ${
            disableHover ? "no-hover" : ""
          }`}
          ref={listRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUpOrLeave}
          onPointerLeave={onPointerUpOrLeave}
          onPointerCancel={onPointerUpOrLeave}
          onWheel={(e) => {
            if (e.deltaY === 0) return;
            e.currentTarget.scrollLeft += e.deltaY; // 세로 휠 → 가로 이동
            e.preventDefault();
          }}
          style={{
            display: "flex",
            gap: `${gap}px`,
            overflowX: "auto",
            scrollBehavior: "smooth",
            WebkitOverflowScrolling: "touch",
            minWidth: 0,
            maxWidth: "100%",
            cursor: "grab",
            touchAction: "none",
          }}
        >
          {/* <div className="nextbox">
            <IonIcon name="IoChevronForward" />
          </div> */}
          {tabs.length > 0 ? (
            tabs.map((t) => (
              <button
                key={t.id}
                data-tab-id={t.id}
                className={t.id === selectedId ? "on" : ""}
                style={{ flex: `0 0 ${btnWidth}`, minWidth: btnWidth }}
                onClick={(e) => {
                  e.currentTarget.blur();
                  setSelectedId(t.id);
                  markUserClick();
                }}
                title={t.name}
              >
                {t.name}
              </button>
            ))
          ) : (
            <span>-</span>
          )}
        </div>
      </div>

      <div className="envboxs small">
        {data ? (
          <>
            <div className="env">
              <div className="title">온도</div>
              <div className="amount">{data.temperature ?? "-"}</div>
              <div className="unit">°C</div>
            </div>
            <div className="env">
              <div className="title">습도</div>
              <div className="amount">{data.humidity ?? "-"}</div>
              <div className="unit">%</div>
            </div>
            <div className={`env ${envLevel("fineDustLevel")}`}>
              <div className="title">미세먼지</div>
              <div className="amount">{data.fineDust ?? "-"}</div>
              <div className="unit">㎍/m3</div>
            </div>
            <div className={`env ${envLevel("ultrafineDustLevel")}`}>
              <div className="title">초미세먼지</div>
              <div className="amount">{data.ultrafineDust ?? "-"}</div>
              <div className="unit">㎍/m3</div>
            </div>
            <div className={`env ${envLevel("coLevel")}`}>
              <div className="title">일산화탄소</div>
              <div className="amount">{data.co ?? "-"}</div>
              <div className="unit">ppm</div>
            </div>
            <div className={`env ${envLevel("o2Level")}`}>
              <div className="title">산소</div>
              <div className="amount">{data.o2 ?? "-"}</div>
              <div className="unit">%</div>
            </div>
            <div className={`env ${envLevel("h2SLevel")}`}>
              <div className="title">황화수소</div>
              <div className="amount">{data.h2S ?? "-"}</div>
              <div className="unit">ppm</div>
            </div>
            <div className={`env ${envLevel("ch4Level")}`}>
              <div className="title">메탄가스</div>
              <div className="amount">{data.ch4 ?? "-"}</div>
              <div className="unit">%</div>
            </div>
          </>
        ) : (
          <>
            <div className="env">
              <div className="title">온도</div>
              <div className="amount">-</div>
              <div className="unit">°C</div>
            </div>
            <div className="env">
              <div className="title">습도</div>
              <div className="amount">-</div>
              <div className="unit">%</div>
            </div>
            <div className="env">
              <div className="title">미세먼지</div>
              <div className="amount">-</div>
              <div className="unit">㎍/m3</div>
            </div>
            <div className="env">
              <div className="title">초미세먼지</div>
              <div className="amount">-</div>
              <div className="unit">㎍/m3</div>
            </div>
            <div className="env">
              <div className="title">일산화탄소</div>
              <div className="amount">-</div>
              <div className="unit">ppm</div>
            </div>
            <div className="env">
              <div className="title">산소</div>
              <div className="amount">-</div>
              <div className="unit">%</div>
            </div>
            <div className="env">
              <div className="title">황화수소</div>
              <div className="amount">-</div>
              <div className="unit">ppm</div>
            </div>
            <div className="env">
              <div className="title">메탄가스</div>
              <div className="amount">-</div>
              <div className="unit">%</div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
